import { RadioGroup as RdxRadioGroup } from 'radix-ui';
import { ComponentProps, forwardRef } from 'react';
import cn from '../../app/utils/cn';

interface IRadioGroupProps {
  value: string;
  onChange: (value: string) => void;
  name?: string;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  children: React.ReactNode;
}

export function RadioGroup({
  value,
  onChange,
  name,
  disabled,
  className,
  'aria-label': ariaLabel,
  children,
}: IRadioGroupProps) {
  return (
    <RdxRadioGroup.Root
      value={value}
      onValueChange={onChange}
      name={name}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn('flex flex-col gap-2', className)}
    >
      {children}
    </RdxRadioGroup.Root>
  );
}

interface IRadioGroupItemProps extends Omit<
  ComponentProps<typeof RdxRadioGroup.Item>,
  'children'
> {
  value: string;
  label: React.ReactNode;
  id?: string;
}

export const RadioGroupItem = forwardRef<
  HTMLButtonElement,
  IRadioGroupItemProps
>(({ value, label, id, disabled, className, ...props }, ref) => {
  const itemId = id ?? `radio-${value}`;
  return (
    <label
      htmlFor={itemId}
      className={cn(
        'flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm transition-colors',
        'border-border-input bg-surface-input text-text-primary',
        'hover:border-green-600 hover:bg-green-600/5',
        'has-[button[data-state=checked]]:border-green-600 has-[button[data-state=checked]]:bg-green-600/10 dark:has-[button[data-state=checked]]:border-green-400',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      <RdxRadioGroup.Item
        ref={ref}
        id={itemId}
        value={value}
        disabled={disabled}
        className={cn(
          'aspect-square h-4 w-4 shrink-0 cursor-pointer rounded-full border-2 transition-colors outline-none',
          'border-border-input bg-surface-input',
          'data-[state=checked]:border-green-600 dark:data-[state=checked]:border-green-500',
          'focus-visible:ring-2 focus-visible:ring-green-500/50',
          'disabled:cursor-not-allowed',
        )}
        {...props}
      >
        <RdxRadioGroup.Indicator className="relative flex h-full w-full items-center justify-center after:block after:h-2 after:w-2 after:rounded-full after:bg-green-600 after:content-[''] dark:after:bg-green-500" />
      </RdxRadioGroup.Item>
      <span>{label}</span>
    </label>
  );
});

RadioGroupItem.displayName = 'RadioGroupItem';
